/**
 * The two things a dish row on the table board can do.
 *
 * Kept out of the row so the row is layout. `advance` walks the chef's cycle
 * and stops at READY; `serve` is the bar's one-step path for something that
 * comes out of a fridge.
 *
 * Feature 252 — a collapsed line of identical dishes passes its portions as
 * `group`, and both actions then apply to the whole line: identical plates leave
 * the pass together, and a cook pressing "Pronto" on a line of three means all
 * three. Portions already further along are left where they are, so one press
 * can never drag a plate backwards.
 */
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { itemsApi } from "../api/client";
import { DISH_ROW_STATUS_CYCLE } from "../lib/dish-row";

export function useDishRowActions(item: any, group?: readonly any[]) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["kitchen-orders"] });
    queryClient.invalidateQueries({ queryKey: ["kitchen-station-orders"] });
  }, [queryClient]);

  /** The portions this row stands for. A plain row stands for itself. */
  const portions: readonly any[] = group && group.length > 0 ? group : [item];

  const advance = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const next = DISH_ROW_STATUS_CYCLE[item?.status];
      if (!next || loading) return;

      setLoading(true);
      try {
        // Every portion still behind the target moves to it. Portions already
        // ahead are untouched: one press must never walk a plate backwards.
        const behind = portions.filter(
          (p) => DISH_ROW_STATUS_CYCLE[p?.status] !== undefined && p?.status === item.status,
        );
        for (const portion of behind.length > 0 ? behind : [item]) {
          if (portion.status === "PREPARING") await itemsApi.bump(portion.id);
          else await itemsApi.updateStatus(portion.id, next);
        }
        invalidate();
      } finally {
        setLoading(false);
      }
    },
    [invalidate, item, loading, portions],
  );

  const serve = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (loading) return;
      setLoading(true);
      try {
        for (const portion of portions) {
          if (portion.status === "SERVED" || portion.status === "CANCELLED") continue;
          await itemsApi.updateStatus(portion.id, "SERVED");
        }
        invalidate();
      } finally {
        setLoading(false);
      }
    },
    [invalidate, loading, portions],
  );

  return { loading, advance, serve };
}
